import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectCardProps } from "../../components/projectCard/ProjectCard";
import AppCard from "../../components/AppCard/AppCard";
import "./MfaAppsPage.scss";
import {
  AddRoundedIcon,
  GridViewIcon,
  ListViewIcon,
} from "../../components/icons";
import {
  PulseButton,
  PulsePopupContainerSize,
  PulseDropdown,
} from "@pulselabs-ai/shared-fe-ui";
import CustomDialog from "@pl-flight-recorder/web/components/customDialog/CustomDialog";
import AddProjectModal from "../../components/AddProjectModal/AddProjectModal";
import InstallSDKModal from "../../components/InstallSDKModal/InstallSDKModal";
import MfaAppService from "../../services/MfaAppService";
import { FlightRecorderProjectDTO } from "../../types/flight-recorder.types";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/store";
import { setSelectedProject } from "../../store/slices/onboardingSlice";
import { selectUserActiveWorkspace } from "../../store/slices/userSlice";
import { getAuthUrl } from "@pl-flight-recorder/web/utils/authUtils";
import Loader from "../../components/PulseLoader/loader";
import MfaShareAppModal from "../../components/dialogs/MfaShareAppModal/MfaShareAppModal";
import { useArchiveAppConfirmation } from "../../hooks/useArchiveAppConfirmation";
import ArchiveAppDialog from "../../components/ArchiveAppDialog/ArchiveAppDialog";

// View type enum
type ViewType = "grid" | "list";

export default function MfaAppsPage() {
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSDKModalOpen, setIsSDKModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectCardProps | null>(
    null,
  );
  const [projects, setProjects] = useState<FlightRecorderProjectDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user: user } = useSelector((state: RootState) => state.user);
  const activeWorkspace = useSelector(selectUserActiveWorkspace);
  const [statusFilter, setStatusFilter] = useState<string>("");
  // const [accessFilter, setAccessFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("modifiedDate");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const dispatch = useDispatch();
  const [showAddProject, setShowAddProject] = useState(false);
  const [viewType, setViewType] = useState<ViewType>("grid");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareProjectId, setShareProjectId] = useState<string | null>(null);

  const {
    isArchiveConfirmOpen,
    itemToArchive,
    isArchiving,
    openArchiveConfirm,
    closeArchiveConfirm,
    confirmArchiveAction,
  } = useArchiveAppConfirmation({
    onSuccess: () => fetchProjects(),
  });

  const fetchProjects = useCallback(async () => {
    console.log("Fetching projects for workspace:", activeWorkspace);
    try {
      console.log("user", user);
      console.log("activeWorkspace", activeWorkspace);
      console.log("token", user?.token);
      if (!user?.id) {
        return;
      }

      if (!activeWorkspace?.id || activeWorkspace.id === -1) {
        setProjects([]);
        setError("No workspace selected. Please select a workspace.");
        return;
      }

      const mfaAppService = MfaAppService.getInstance();
      const fetchedProjects = await mfaAppService.getProjectsByWorkspaceId(
        activeWorkspace.id.toString(),
        user.id.toString(),
      );
      setProjects(Array.isArray(fetchedProjects) ? fetchedProjects : []);

      if (fetchedProjects && fetchedProjects.length > 0) {
        dispatch(setSelectedProject(fetchedProjects[0].project.id));
      }
    } catch (err) {
      setProjects([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeWorkspace, dispatch]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!loading && user?.token && projects.length === 0) {
      const timer = setTimeout(() => {
        setShowAddProject(true);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [loading, user?.token, projects.length]);

  if (loading || !user?.token) return <Loader />;
  if (error) return <div>Error: {error}</div>;

  const handleAddProject = async (projectData: {
    title: string;
    description: string;
  }) => {
    try {
      if (!user?.id || !activeWorkspace?.id) return;

      const projectInput = {
        projectName: projectData.title,
        projectObjective: projectData.description,
        workspaceId: activeWorkspace.id.toString(),
        userId: user.id.toString(),
        status: "active",
      };

      const mfaAppService = MfaAppService.getInstance();
      await mfaAppService.createProject(projectInput);

      // Set loading to true before fetching updated projects
      setLoading(true);

      // Refresh project list
      const fetchedProjects = await mfaAppService.getProjectsByWorkspaceId(
        activeWorkspace.id.toString(),
        user.id.toString(),
      );
      setProjects(fetchedProjects);
      setIsAddModalOpen(false);

      // Set loading back to false after fetching is complete
      setLoading(false);
    } catch (err) {
      console.error("Failed to create project:", err);
      // Also ensure loading is false in case of error
      setLoading(false);
    }
  };

  const handleEditProject = async (projectData: {
    title: string;
    description: string;
  }) => {
    if (!editingProject?.id || !user?.id || !activeWorkspace?.id) return;

    try {
      const mfaAppService = MfaAppService.getInstance();
      const projectInput = {
        projectName: projectData.title,
        projectObjective: projectData.description,
        workspaceId: activeWorkspace.id.toString(),
        userId: user.id.toString(),
        status: "active",
      };
      await mfaAppService.updateProject(editingProject.id, projectInput);

      // Set loading to true before fetching updated projects
      setLoading(true);

      const fetchedProjects = await mfaAppService.getProjectsByWorkspaceId(
        activeWorkspace.id.toString(),
        user.id.toString(),
      );
      setProjects(fetchedProjects);
      setEditingProject(null);

      // Set loading back to false after fetching is complete
      setLoading(false);
    } catch (err) {
      console.error("Failed to update project:", err);
      // Also ensure loading is false in case of error
      setLoading(false);
    }
  };

  const handleClickEdit = (project: ProjectCardProps) => {
    setEditingProject(project);
  };

  const closeEditModal = () => {
    setEditingProject(null);
  };

  const filteredProjects = Array.isArray(projects)
    ? projects
        .filter((project) => {
          const matchesStatus =
            statusFilter === "all" ||
            statusFilter === "" ||
            project.project.status.toLowerCase() === statusFilter;
          // const matchesAccess =
          //   accessFilter === "all" ||
          //   accessFilter === project.projectAccess.access.toLowerCase();
          return matchesStatus; // && matchesAccess;
        })
        .sort((a, b) => {
          const dateA = new Date(
            a.project.updatedAt || a.project.createdAt,
          ).getTime();
          const dateB = new Date(
            b.project.updatedAt || b.project.createdAt,
          ).getTime();

          const sortOrder = sortBy === "modifiedDate" ? -1 : 1;
          const comparison = (dateA - dateB) * sortOrder;

          console.log("Sorting:", {
            a: a.project.projectName,
            b: b.project.projectName,
            dateA: new Date(
              a.project.updatedAt || a.project.createdAt,
            ).toISOString(),
            dateB: new Date(
              b.project.updatedAt || b.project.createdAt,
            ).toISOString(),
            sortBy,
            result: comparison,
          });

          return comparison;
        })
    : [];

  const handleDeleteProject = (project: { id: string; name: string }) => {
    openArchiveConfirm(project);
  };

  const handleOpenShareModal = (projectId: string) => {
    setShareProjectId(projectId);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareProjectId(null);
  };

  return (
    <div className="projects-container page-container">
      <div className="projects-header">
        <div className="app-title-row">
          {user?.token && <div className="headline-xl">Apps</div>}
          {projects.length > -1 && user?.token && (
            <PulseButton
              startIcon={<AddRoundedIcon />}
              onClick={() => setIsAddModalOpen(true)}
            >
              ADD AN APP
            </PulseButton>
          )}
        </div>
      </div>
      {projects.length > 0 && (
        <div className="filters-container">
          <div className="filters-left">
            <PulseDropdown
              selectedValue={statusFilter}
              onChange={(value: string) => setStatusFilter(value)}
              placeholder="Status"
              options={[
                { label: "All", value: "all" },
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" },
              ]}
              style={{
                width: "200px",
                backgroundColor: "white",
                borderRadius: "8px",
              }}
            />
          </div>
          <div className="filters-right">
            <div className="sort-wrapper">
              <span className="sort-by-text">Sort by:</span>
              <PulseDropdown
                selectedValue={sortBy}
                isMinimized={true}
                onChange={(value: string) => setSortBy(value)}
                options={[
                  { label: "↓ Modified", value: "modifiedDate" },
                  { label: "↑ Modified", value: "modifiedDateDesc" },
                ]}
              />
            </div>

            <div className="view-controls">
              <button
                className={`view-control-button ${viewType === "grid" ? "active" : ""}`}
                onClick={() => setViewType("grid")}
                aria-label="Grid View"
                title="Grid View"
              >
                <GridViewIcon />
              </button>
              <button
                className={`view-control-button ${viewType === "list" ? "active" : ""}`}
                onClick={() => setViewType("list")}
                aria-label="List View"
                title="List View"
              >
                <ListViewIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`projects-grid ${viewType === "list" ? "list-view" : ""}`}
      >
        {projects.length === 0 && !user?.token && (
          <PulseButton
            className="login-button"
            onClick={() => (window.location.href = getAuthUrl())}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            Log In
          </PulseButton>
        )}
        {projects.length > 0 &&
          filteredProjects.map((projectDTO) => (
            <AppCard
              key={projectDTO.project.id}
              id={projectDTO.project.id}
              appTitle={projectDTO.project.projectName}
              appDescription={projectDTO.project.projectObjective}
              bannerText={projectDTO.project.status.toUpperCase()}
              onClickOpenApp={() =>
                navigate(`/project/${projectDTO.project.id}`)
              }
              onClickEdit={() =>
                handleClickEdit({
                  id: projectDTO.project.id,
                  cardTitle: projectDTO.project.projectName,
                  cardDescription: projectDTO.project.projectObjective,
                  bannerText: projectDTO.project.status,
                })
              }
              onClickShare={() => handleOpenShareModal(projectDTO.project.id)}
              onClickSettings={() =>
                navigate(`/project/${projectDTO.project.id}/settings`)
              }
              onClickDelete={() =>
                handleDeleteProject({
                  id: projectDTO.project.id,
                  name: projectDTO.project.projectName,
                })
              }
              data={projectDTO}
            />
          ))}
      </div>

      {projects.length === 0 && user?.token && showAddProject && (
        <div className="empty-state">
          <div className="empty-state-content">
            <div className="sub-headline-l">Get Started</div>
            <div className="body-xl">
              Apps allow you to receive realtime feedback and live monitoring of
              your mobile and web applications.
            </div>
          </div>

          <div className="empty-state-video">
            <div className="video-container">
              <img src="/images/AppsVideo.jpeg" alt="Apps Video" />
            </div>
          </div>
        </div>
      )}

      <div className="padding-bottom"></div>

      <CustomDialog
        isOpen={isSDKModalOpen}
        onClose={() => setIsSDKModalOpen(false)}
        containerSize={PulsePopupContainerSize.CUSTOM}
        showCloseIcon={true}
        isBackgroundClose={false}
        roundedCorners={true}
      >
        <InstallSDKModal
          isOpen={isSDKModalOpen}
          onClose={() => {
            setIsSDKModalOpen(false);
            setSelectedProjectId(null);
          }}
          projectId={selectedProjectId || ""}
        />
      </CustomDialog>

      <CustomDialog
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        containerSize={PulsePopupContainerSize.CUSTOM}
        showCloseIcon={true}
        isBackgroundClose={false}
        roundedCorners={true}
      >
        <AddProjectModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddProject}
        />
      </CustomDialog>

      <CustomDialog
        isOpen={!!editingProject}
        onClose={closeEditModal}
        containerSize={PulsePopupContainerSize.CUSTOM}
        showCloseIcon={true}
        isBackgroundClose={false}
        roundedCorners={true}
      >
        <AddProjectModal
          onClose={closeEditModal}
          onSubmit={handleEditProject}
          isEditMode={true}
          initialData={{
            title: editingProject?.cardTitle || "",
            description: editingProject?.cardDescription || "",
          }}
        />
      </CustomDialog>

      <ArchiveAppDialog
        isOpen={isArchiveConfirmOpen}
        onClose={closeArchiveConfirm}
        onConfirm={confirmArchiveAction}
        appName={itemToArchive?.name}
        isArchiving={isArchiving}
      />

      <MfaShareAppModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        projectId={shareProjectId}
      />
    </div>
  );
}
